/*
 * Briefing videos kept on the phone for the event (routes/event_briefings.py;
 * Part III section 17, PR12): a forest has no signal, so "Prepare for the
 * event" downloads every briefing into Cache Storage beforehand, and the
 * briefing plays from there. Keyed by briefing id, never by its URL (a
 * presigned URL changes with every listing).
 *
 * `caches` is injectable for tests; where Cache Storage is missing (an old
 * browser, a private window) nothing is kept and the video plays online.
 */
export const CACHE_NAME = 'pitriot-briefings-v1';

const keyOf = (id) => `/briefing-cache/${id}`;

function storageOf(store) {
  if (store) return store;
  try { return globalThis.caches || null; } catch { return null; }
}

/** Download each briefing not kept yet; `onProgress(done, total)` after each. */
export async function prepareBriefings(briefings, { onProgress, store, fetcher = globalThis.fetch } = {}) {
  const caches = storageOf(store);
  if (!caches || !fetcher) return { kept: 0, skipped: briefings.length, failed: 0 };
  const cache = await caches.open(CACHE_NAME);
  let done = 0;
  let kept = 0;
  let failed = 0;
  for (const b of briefings) {
    try {
      const have = await cache.match(keyOf(b.briefing_id));
      if (!have) {
        const response = await fetcher(b.url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await cache.put(keyOf(b.briefing_id), response);
      }
      kept += 1;
    } catch {
      failed += 1;
    }
    done += 1;
    onProgress?.(done, briefings.length);
  }
  return { kept, skipped: 0, failed };
}

/** A URL a <video> can play: the kept copy (an object URL) when there is one,
 *  else the briefing's own URL. `revoke` frees the object URL. */
export async function playableSource(briefing, { store, createUrl = (b) => URL.createObjectURL(b) } = {}) {
  const caches = storageOf(store);
  if (caches) {
    try {
      const cache = await caches.open(CACHE_NAME);
      const hit = await cache.match(keyOf(briefing.briefing_id));
      if (hit) {
        const url = createUrl(await hit.blob());
        return { src: url, cached: true, revoke: () => URL.revokeObjectURL?.(url) };
      }
    } catch {
      /* fall through to the network */
    }
  }
  return { src: briefing.url, cached: false, revoke: () => {} };
}

/** Which of these briefings are kept on this device. */
export async function keptIds(briefings, { store } = {}) {
  const caches = storageOf(store);
  if (!caches) return new Set();
  const cache = await caches.open(CACHE_NAME);
  const out = new Set();
  for (const b of briefings) {
    if (await cache.match(keyOf(b.briefing_id))) out.add(b.briefing_id);
  }
  return out;
}
